import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch, setAccessToken, setOnUnauthorized, refreshAccessToken, wasRefreshDenied } from "@/lib/apiClient";

interface User {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  plan: "free" | "premium";
  isPremium: boolean;
  /** Desconto na primeira cobrança que o admin deu pra essa conta, enquanto valer. */
  discountUntil: string | null;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (name: string, email: string, phone: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// O cookie de sessão é httpOnly, então o navegador não tem como saber sozinho se existe sessão -
// só perguntando pro servidor. Essa marca guarda a última resposta ("1" tinha sessão, "0" não
// tinha) pra não segurar a primeira tela de quem não está logado esperando essa pergunta.
const SESSION_HINT_KEY = "controlenext-session";

function readSessionHint() {
  try {
    return localStorage.getItem(SESSION_HINT_KEY);
  } catch {
    return null;
  }
}

function writeSessionHint(hasSession: boolean) {
  try {
    localStorage.setItem(SESSION_HINT_KEY, hasSession ? "1" : "0");
  } catch {
    // localStorage indisponível (modo privado etc.) - só volta a perguntar pro servidor toda vez.
  }
}

// Lido no carregamento do módulo, antes de o ThemeProvider gravar o tema: se nem o tema existe
// ainda, esse navegador nunca abriu o app - é visitante novo e não tem como ter sessão.
const isFirstVisitEver = (() => {
  try {
    return localStorage.getItem("controlenext-theme") === null;
  } catch {
    return false;
  }
})();

/** Precisa perguntar pro servidor antes de mostrar qualquer tela? Só se pode existir sessão. */
function mayHaveSession() {
  const hint = readSessionHint();
  if (hint !== null) return hint === "1";
  // Sem marca: ou é visitante novo, ou alguém que já usava o app antes de a marca existir.
  return !isFirstVisitEver;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(mayHaveSession);
  const userIdRef = useRef<string | null>(null);

  // Troca de identidade (visitante <-> conta, ou uma conta por outra): o que está em cache é de
  // outra pessoa - ou são os dados de exemplo da demonstração - e não pode aparecer pra nova.
  const applyUser = useCallback(
    (next: User | null) => {
      const nextId = next?.id ?? null;
      if (nextId !== userIdRef.current) {
        userIdRef.current = nextId;
        void queryClient.resetQueries();
      }
      setUser(next);
    },
    [queryClient]
  );

  const refreshUser = useCallback(async () => {
    const data = await apiFetch<{ user: User }>("/auth/me");
    applyUser(data.user);
  }, [applyUser]);

  useEffect(() => {
    setOnUnauthorized(() => {
      setAccessToken(null);
      if (wasRefreshDenied()) writeSessionHint(false);
      applyUser(null);
    });

    if (!mayHaveSession()) {
      writeSessionHint(false);
      return;
    }

    (async () => {
      const token = await refreshAccessToken();
      if (token) {
        try {
          await refreshUser();
          writeSessionHint(true);
        } catch {
          // Sessão existe mas não deu pra carregar a conta agora: segue como visitante nessa
          // visita, sem token (pra não misturar dado real com a demonstração), e tenta de novo
          // na próxima.
          setAccessToken(null);
          applyUser(null);
        }
      } else if (wasRefreshDenied()) {
        // Só marca "sem sessão" quando o servidor disse isso - falha de rede não desloga ninguém.
        writeSessionHint(false);
      }
      setIsLoading(false);
    })();
  }, [refreshUser, applyUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await apiFetch<{ user: User; accessToken: string }>("/auth/login", {
        method: "POST",
        body: { email, password },
      });
      setAccessToken(data.accessToken);
      writeSessionHint(true);
      applyUser(data.user);
      return data.user;
    },
    [applyUser]
  );

  const register = useCallback(
    async (name: string, email: string, phone: string, password: string) => {
      const data = await apiFetch<{ user: User; accessToken: string }>("/auth/register", {
        method: "POST",
        body: { name, email, phone, password },
      });
      setAccessToken(data.accessToken);
      writeSessionHint(true);
      applyUser(data.user);
    },
    [applyUser]
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } finally {
      setAccessToken(null);
      writeSessionHint(false);
      applyUser(null);
    }
  }, [applyUser]);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return ctx;
}
